
import React, { useMemo, useState, useCallback, useEffect, ReactElement } from 'react';
import { Card, Nav, Tab } from 'react-bootstrap';
import IntlMessages from 'helpers/IntlMessages';
import { MapAndLocationName } from 'components/Map/MapAndLocationName';
import SpecimenOverviewTabMobile from 'pages/Bioregion/Specimen/SpecimenOverviewTabMobile';
import SpecimenInfoMobile from './SpecimenInfoMobile';
import { RouteProps, useHistory, useLocation, useParams } from 'react-router-dom';
import { setTabFromLocation } from 'helpers/utils-typescript';
import { Speciman } from 'services/graphqltypes';
import TreeMapIcon from 'assets/icons/mapping-icons/TreeMapIcon';
import PoiOverviewTabMobile from './PoiOverviewTabMobile';
import { MapMarkerNumber } from 'pages/Commons/Quest/RouteMap';
import { getOsmTagIconAndName, OsmTagType } from 'pages/Mapping/SelectPoi';

export interface SpecimenDetailMobileGeneralProps {
  hasEditPermission?: boolean;
  specimanBySlug: Speciman;
  setContextHelpId?: React.Dispatch<React.SetStateAction<string>>,
  setShowContextHelpModal?: React.Dispatch<React.SetStateAction<boolean>>,
  showInQuestStop?: boolean
  goBackLink?: string | RouteProps['location'];
}

interface SpecimenDetailMobileLayoutProps extends SpecimenDetailMobileGeneralProps {
  setSpecimenData?: React.Dispatch<React.SetStateAction<{
    dataWtUnits: string,
    icon: ReactElement<{ height: string, className: string }>,
    titleTranslationId: string,
  }>>;
}


const SpecimenDetailsMobileLayout = (
  {
    hasEditPermission,
    specimanBySlug,
    setContextHelpId,
    setShowContextHelpModal,
    setSpecimenData,
    showInQuestStop = false,
  }: SpecimenDetailMobileLayoutProps) => {


  // Tabs
  const history = useHistory();
  const { commonsSlug } = useParams() as any;
  const { hash, state } = useLocation<{ from: RouteProps['location'] }>();
  const tabs = useMemo(() => {
    return !showInQuestStop ? [
      { eventKey: 'overview', translationId: 'bioregion.overview' },
      { eventKey: 'info', translationId: 'library.info' },
      { eventKey: 'map', translationId: 'specimen.map' },
    ] :
      [
        { eventKey: 'overview', translationId: 'bioregion.overview' },
        { eventKey: 'info', translationId: 'library.info' },
      ]
      ;
  }, [showInQuestStop]);
  const [activeTab, setActiveTab] = useState(tabs[0].eventKey);
  const handleTabChange = useCallback((tabName: string) => { history.push(`#${tabName}`); }, [history]);
  useEffect(() => {
    const hashForTab = tabs.map((tab) => `#${tab.eventKey}`).includes(hash) ? hash : tabs[0].eventKey;  // redirect to overview tab if hash is not valid

    if (hashForTab) { setTabFromLocation(hashForTab, tabs.map((tab) => tab.eventKey), setActiveTab); }
  }, [hash, tabs]);


  // layout calculation
  const maxSpecimenCardNavHeight = '2rem';
  const appNavBarAndPaddingAroundSpecimenCard = '80px';
  const { tabContentHeight, tabContainerHeight } = useMemo(() => {
    const calculatedTabContainerHeight = `calc(100vh - ${appNavBarAndPaddingAroundSpecimenCard})`
    const calculatedTabContentHeight = `calc(100vh - ${appNavBarAndPaddingAroundSpecimenCard} - ${maxSpecimenCardNavHeight})`;
    return {
      tabContentHeight: showInQuestStop ? `calc(100% - ${maxSpecimenCardNavHeight})` : calculatedTabContentHeight,
      tabContainerHeight: showInQuestStop ? 'calc(100% - 5.7rem)' : calculatedTabContainerHeight, /* 5.7rem is approximately the height of congratulation message div */
    };
  }, [maxSpecimenCardNavHeight, appNavBarAndPaddingAroundSpecimenCard, showInQuestStop]);

  const goBackLink = state?.from || (commonsSlug ? `/commons/${commonsSlug}#specimens` : '/map'); // redirect to commons page if no from location provided, or to map page if no commons slug provided
  
  return (
    <Tab.Container defaultActiveKey='overview' activeKey={activeTab} id='tabs-container'>

      <Card style={{ minHeight: 'calc(20rem/0.65 + 2rem)', height: tabContainerHeight }} className='d-flex flex-column'>

        <Tab.Content style={{ height: tabContentHeight, minHeight: 'calc(20rem/0.65)' }}> {/* min height of top half of the card (65% of the total heighth) is 20rem */}
          {/* Overview tab */}
          <Tab.Pane eventKey={tabs[0].eventKey} className='h-100'>
            <div className='d-flex flex-column h-100'>
              {specimanBySlug.type === 'POI'
                ? <PoiOverviewTabMobile
                  goBackLink={goBackLink}
                  hasEditPermission={hasEditPermission}
                  setContextHelpId={setContextHelpId}
                  setShowContextHelpModal={setShowContextHelpModal}
                  setSpecimenData={setSpecimenData}
                  specimanBySlug={specimanBySlug}
                  isPrintableCard={false}
                  showInQuestStop={showInQuestStop}
                />
                : <SpecimenOverviewTabMobile
                  goBackLink={goBackLink}
                  hasEditPermission={hasEditPermission}
                  setContextHelpId={setContextHelpId}
                  setShowContextHelpModal={setShowContextHelpModal}
                  setSpecimenData={setSpecimenData}
                  specimanBySlug={specimanBySlug}
                  isPrintableCard={false}
                  showInQuestStop={showInQuestStop}
                />
              }
            </div>
          </Tab.Pane>

          {/* Info tab */}
          <Tab.Pane eventKey={tabs[1].eventKey} className='h-100'>
            <div style={{ overflowY: 'auto' }} className='d-flex flex-column h-100'>
              <SpecimenInfoMobile
                showInQuestStop={showInQuestStop}
                goBackLink={goBackLink}
                hasEditPermission={hasEditPermission}
                specimanBySlug={specimanBySlug}
              />
            </div>
          </Tab.Pane>

          {/* Map tab */}
          {!showInQuestStop && <Tab.Pane eventKey={tabs[2].eventKey} className='h-100'>
            <MapAndLocationName
              containerHeight={tabContentHeight}
              district={specimanBySlug.district as JSON}
              coordinates={[specimanBySlug.coordinates?.x, specimanBySlug.coordinates?.y]}
              markerIcon={
                specimanBySlug.type !== 'POI'
                  ? <TreeMapIcon size={40} />
                  : <MapMarkerNumber // this is not really working out with the offset here; the offset should be added as LocationMapCard component
                    number={getOsmTagIconAndName(specimanBySlug.osmTags as OsmTagType, '30px').icon}
                    color='white'
                    borderColor='#60b6aa' // ARK green
                    width='40px'
                    height='40px'
                  />
              }
            />
          </Tab.Pane>
          }
        </Tab.Content>


        {/* Tabs */}
        <Nav
          variant='tabs'
          className='separator-tabs nav-fill mx-0 w-100 mb-0 mt-auto'
          style={{ maxHeight: maxSpecimenCardNavHeight, borderRadius: '0.75rem' }}
        >
          {tabs.map((tab, index) => {
            return (
              <Nav.Item
                key={index}
                className='px-2'
                style={{ maxHeight: '100%' }}
              >
                <Nav.Link
                  eventKey={tab.eventKey}
                  onClick={() => handleTabChange(tab.eventKey)}
                  className='mx-0 pt-0 pb-0 mb-0 d-flex flex-column'
                  style={{ maxHeight: '100%' }}
                >
                  <div className='my-auto py-1 text-truncate'>
                    <IntlMessages id={tab.translationId} />
                  </div>
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
      </Card>
    </Tab.Container>
  );
};
export default SpecimenDetailsMobileLayout;
